/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from 'react'
import {useStaticQuery, graphql} from 'gatsby'
import {css} from '@emotion/react'
import PropTypes from 'prop-types'
import {logDplWithExtensionParams, EVENTS} from '../util/dpl'
import {isFirefox} from '../util/userAgent'
import {clearLocalStorage} from '../util/extensionMetadata'
import Layout from '../components/layout'
import SEO from '../components/seo'
import {convertDatoCmsImgPathsToLocal} from '../util/datoCmsDataFactory'
import {globalStylesProtection, colors} from '../constants'

import HeroProtectionSuccess from '../components/HeroProtectionSuccess'
import InstructionsInstallProtection from '../components/InstructionsInstallProtection'
import Footer from '../components/templates/protection-v2/Footer'

import StartpageLogoRedesign from '../images/startpage-logo-redesign.svg'
import SideBySide from '../components/SideBySide'

const CONTAINER_STYLES = `
    width: 1200px;
    margin: auto;
    padding: 0 1rem;
`

const ProtectionSuccess = ({languageCode = 'en'}) => {
    const site = useStaticQuery(graphql`
        query ProtectionSuccessPage {
            site {
                siteMetadata {
                    ...ProtectionSuccess
                }
            }
        }
    `)
    const {
        successSection1Tagline,
        successSection1Description,
        successSection2Pills,
        footerBlogAsset,
        footerLinks,
    } = site.site.siteMetadata.privacyprotect[languageCode]

    const homeLink = `https://www.startpage.com/${languageCode}`

    const [hasFiredSuccess, setHasFiredSuccess] = useState(false)
    const [firefoxFlag, setFirefoxFlag] = useState(false)
    const [instructionsComponent, setInstructionsComponent] = useState(
        <HeroProtectionSuccess languageCode={languageCode} />,
    )
    const [shouldFadeInInstructions, setShouldFadeInInstructions] = useState(false)

    useEffect(() => {
        if (!hasFiredSuccess) {
            logDplWithExtensionParams(EVENTS.StartpagePageLoad).then(() => {
                clearLocalStorage()
            })
            setHasFiredSuccess(true)
            if (isFirefox()) {
                setInstructionsComponent(<InstructionsInstallProtection languageCode={languageCode} />)
                setFirefoxFlag(true)
            }
            setShouldFadeInInstructions(true)
        }
    }, [hasFiredSuccess])

    return (
        <Layout globalStyles={globalStylesProtection}>
            <SEO title={languageCode === `de` ? `Glückwunsch` : `Success!`} description={successSection1Description} />

            <section
                className="header"
                css={css`
                    ${CONTAINER_STYLES}
                    padding: 50px 1rem 3rem;
                    color: ${colors.midnight};
                    display: flex;
                    align-items: center;
                    svg {
                        height: 25px;
                        width: 110px;
                    }
                    h1 {
                        margin: -8px 0 0 1rem;
                        font-size: 18px;
                    }
                `}
            >
                <a href={homeLink} rel="noopener noreferrer">
                    <StartpageLogoRedesign />
                </a>
                <h1>{successSection1Tagline}</h1>
            </section>

            <div
                css={css`
                    opacity: ${shouldFadeInInstructions ? 1 : 0};
                    transition: opacity 0.3s ease-in;
                    min-height: 486px;
                `}
            >
                {instructionsComponent}
            </div>

            <section
                className="instructions"
                css={css`
                    background: ${colors.whisper};
                    position: relative;
                    padding: 2rem 0;
                `}
            >
                <div
                    css={css`
                        max-width: 1200px;
                        margin: auto;
                    `}
                >
                    {successSection2Pills.map((item, i) => {
                        const {
                            title,
                            description,
                            image: {url = ``},
                        } = item
                        const isEven = i % 2 === 0
                        return (
                            <SideBySide
                                title={title}
                                description={description}
                                imagePosition={isEven ? 'left' : 'right'}
                                imageSrc={convertDatoCmsImgPathsToLocal(url)}
                                overrideStyles={css`
                                    display: ${firefoxFlag && i === 0 ? 'none' : 'flex'};
                                    img {
                                        margin-left: ${isEven ? '-20px' : ''};
                                    }
                                    h3 {
                                        font-size: 30px;
                                        font-weight: 600px;
                                    }
                                    p {
                                        line-height: 24px;
                                    }
                                `}
                                key={i}
                            />
                        )
                    })}
                </div>
            </section>

            <Footer
                languageCode={languageCode}
                links={footerLinks}
                footerBlogAsset={footerBlogAsset}
                overrideStyles={css`
                    margin-top: 0;
                `}
            />
        </Layout>
    )
}

ProtectionSuccess.propTypes = {
    languageCode: PropTypes.string,
}

ProtectionSuccess.defaultProps = {
    languageCode: `en`,
}

export default ProtectionSuccess
